import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  ModalDialog,
  Tooltip,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";

/**
 * Inline viewer for a service's invite page (connect/play), shown in an
 * Apple-style modal sheet with an iframe instead of a new tab.
 *
 * @param root0
 * @param root0.url
 * @param root0.title
 * @param root0.onClose
 * @returns {ReactElement} ServiceViewer
 */
export function ServiceViewer({
  url,
  title,
  onClose,
}: {
  url: string | null;
  title?: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (url) setLoading(true);
  }, [url]);

  return (
    <Modal
      open={!!url}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            background: "rgba(8, 29, 58, 0.55)",
          },
        },
      }}
    >
      <ModalDialog
        layout="center"
        sx={{
          p: 0,
          overflow: "hidden",
          borderRadius: "20px",
          border: "none",
          width: "min(1100px, calc(100vw - 24px))",
          height: "min(860px, calc(100vh - 48px))",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 12px 24px rgba(0, 0, 0, 0.12), 0 32px 80px rgba(0, 0, 0, 0.28)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: "1px solid var(--mui-palette-divider)",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
            noWrap
            startDecorator={
              <i
                className="fa-solid fa-music"
                style={{ fontSize: 12, opacity: 0.5 }}
              ></i>
            }
          >
            {title ?? "Service"}
          </Typography>
          <Tooltip size="sm" arrow title="Open in a new tab">
            <IconButton
              size="sm"
              variant="soft"
              color="neutral"
              onClick={() => {
                if (url) window.open(url);
              }}
              sx={{ borderRadius: "50%" }}
            >
              <i
                className="fa-solid fa-arrow-up-right-from-square"
                style={{ fontSize: 12 }}
              ></i>
            </IconButton>
          </Tooltip>
          <IconButton
            size="sm"
            variant="soft"
            color="neutral"
            onClick={onClose}
            sx={{ borderRadius: "50%" }}
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: 14 }}></i>
          </IconButton>
        </Box>
        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size="md" />
            </Box>
          ) : (
            []
          )}
          {url ? (
            <iframe
              src={url}
              title={title ?? "Service"}
              onLoad={() => setLoading(false)}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
                position: "relative",
              }}
            />
          ) : (
            []
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
}
